<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="color"
    :minimized="isMinimized"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-3">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle v-if="!isMinimized" class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2" class="collapse-child-wrapper">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-10">
                <VaIcon
                  v-if="childRoute.meta.icon"
                  aria-hidden="true"
                  :name="childRoute.meta.icon"
                  size="18px"
                  :color="iconColor(childRoute)"
                  class="mr-3"
                />
                <VaSidebarItemTitle v-if="!isMinimized" class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useGlobalStore } from '../../stores/global-store'
import { useThemeStore } from '../../stores/theme-store'
import { systemConfigService } from '../../services/systemConfigService'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const globalStore = useGlobalStore()
    const themeStore = useThemeStore()

    const value = ref<boolean[]>([])
    const savedExpandedState = ref<boolean[]>([]) // Store expanded state when collapsed
    const internalMinimized = ref(false) // Internal state for hover collapse
    const minimizedWidth = ref('72px')
    const expandedWidth = ref('280px')

    const enabledModuleCodes = ref<Set<string>>(new Set())
    const modulesLoaded = ref(false)

    const routeNameToModuleCode: Record<string, string> = {
      // WIM
      wim: 'wim',
      'wim-dashboard': 'wim',
      wimdata: 'wim',
      'wim-speed': 'wim',
      'wim-reports': 'wim',

      // Watchdesk
      watchdesk: 'watchdesk',
      'watchdesk-dashboard': 'watchdesk',
      'watchdesk-reports': 'watchdesk',

      // Factory Weighing
      'factory-weighing': 'factory-weighing',
      'factory-dashboard': 'factory-weighing',
      'factory-weigh': 'factory-weighing',
      'factory-mobile-weighing': 'factory-weighing',
      'factory-records': 'factory-weighing',
      'kiosk-weigh': 'factory-weighing',
      'factory-reports': 'factory-weighing',

      // National Weighing
      'national-weighing': 'national-weighing',
      multideck: 'national-weighing',
      lswim: 'national-weighing',
      'mobile-weighing': 'national-weighing',
      'yard-management': 'national-weighing',
      'national-weighing-reports': 'national-weighing',

      // Vehicle Inspection
      'vehicle-inspection': 'vehicle-inspection',
      'inspection-dashboard': 'vehicle-inspection',
      'inspection-records': 'vehicle-inspection',
      'inspection-reports': 'vehicle-inspection',

      // Parking
      parking: 'parking',
      'parking-dashboard': 'parking',
      'parking-records': 'parking',
      'parking-reports': 'parking',

      // Tags
      tags: 'tags',
      'tags-dashboard': 'tags',
      'tags-records': 'tags',
      'tags-management': 'tags',
      'tags-reports': 'tags',

      // Traffic Census
      'traffic-census': 'traffic-census',
      'census-dashboard': 'traffic-census',
      'census-records': 'traffic-census',
      'census-reports': 'traffic-census',

      // Calibration
      calibration: 'calibration',
      'calibration-dashboard': 'calibration',
      'calibration-records': 'calibration',

      // Tolling
      tolling: 'tolling',
      'tolling-dashboard': 'tolling',
      'tolling-records': 'tolling',
      'tolling-rates': 'tolling',
      'tolling-booth': 'tolling',
      'tolling-reports': 'tolling',

      // Prosecution
      prosecution: 'prosecution',
      'prosecution-dashboard': 'prosecution',
      'prosecution-records': 'prosecution',
      'prosecution-reports': 'prosecution',
      'prosecution-settings': 'prosecution',
    }

    const getModuleCodeForRoute = (r: INavigationRoute): string | null => {
      const routeName = r.name
      if (!routeName) return null
      return routeNameToModuleCode[routeName] ?? null
    }

    const isRouteForEnabledModule = (r: INavigationRoute): boolean => {
      const code = getModuleCodeForRoute(r)
      if (!code) {
        // If we don't have a mapping, don't hide it
        return true
      }

      if (!modulesLoaded.value) {
        // Until modules are loaded, show menu to avoid empty sidebar
        return true
      }

      return enabledModuleCodes.value.has(code.toLowerCase())
    }

    const isRouteVisible = (r: INavigationRoute): boolean => {
      if (r.children && r.children.length) {
        return r.children.some((child) => isRouteForEnabledModule(child))
      }
      return isRouteForEnabledModule(r)
    }

    const filteredRoutes = computed<INavigationRoute[]>(() => navigationRoutes.routes.filter(isRouteVisible))

    const loadEnabledModules = async () => {
      try {
        const modules = await systemConfigService.getEnabledModules()
        const set = new Set<string>()
        ;(modules || []).forEach((m: any) => {
          if (m?.code) {
            set.add(String(m.code).toLowerCase())
          }
        })
        enabledModuleCodes.value = set
      } catch (error) {
        console.error('Failed to load enabled modules for sidebar', error)
        enabledModuleCodes.value = new Set()
      } finally {
        modulesLoaded.value = true
      }
    }

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    // Use internal state when auto-collapse is enabled, otherwise use global store
    const isMinimized = computed({
      get: () => {
        if (props.mobile) return false
        return globalStore.autoCollapseEnabled ? internalMinimized.value : globalStore.isSidebarMinimized
      },
      set: (minimized: boolean) => {
        if (globalStore.autoCollapseEnabled) {
          if (minimized) {
            // Save current expanded state before collapsing
            savedExpandedState.value = [...value.value]
            // Close all expanded menus when collapsing
            value.value = filteredRoutes.value.map(() => false)
          } else {
            // Restore saved expanded state when expanding, and ensure active route is expanded
            if (savedExpandedState.value.length > 0) {
              const activeExpanded = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
              // Merge saved state with active route expansion (active routes should always be expanded)
              value.value = savedExpandedState.value.map((saved, index) => saved || activeExpanded[index])
            } else {
              // If no saved state, expand active route
              setActiveExpand()
            }
          }
          internalMinimized.value = minimized
        } else {
          if (minimized) {
            // Save current expanded state before collapsing
            savedExpandedState.value = [...value.value]
            // Close all expanded menus when collapsing
            value.value = filteredRoutes.value.map(() => false)
          } else {
            // Restore saved expanded state when expanding, and ensure active route is expanded
            if (savedExpandedState.value.length > 0) {
              const activeExpanded = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
              // Merge saved state with active route expansion (active routes should always be expanded)
              value.value = savedExpandedState.value.map((saved, index) => saved || activeExpanded[index])
            } else {
              // If no saved state, expand active route
              setActiveExpand()
            }
          }
          globalStore.isSidebarMinimized = minimized
        }
      },
    })

    const hasExpandedMenu = computed(() => value.value.some((expanded) => expanded))

    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      return isMinimized.value ? minimizedWidth.value : expandedWidth.value
    })

    const handleMouseEnter = () => {
      if (!props.mobile && globalStore.autoCollapseEnabled) {
        internalMinimized.value = false
        // Restore saved expanded state when expanding, and ensure active route is expanded
        if (savedExpandedState.value.length > 0) {
          const activeExpanded = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
          // Merge saved state with active route expansion (active routes should always be expanded)
          value.value = savedExpandedState.value.map((saved, index) => saved || activeExpanded[index])
        } else {
          // If no saved state, expand active route
          setActiveExpand()
        }
      }
    }

    const handleMouseLeave = () => {
      if (!props.mobile && globalStore.autoCollapseEnabled) {
        // Save current expanded state before collapsing
        savedExpandedState.value = [...value.value]
        // Close all expanded menus when collapsing
        value.value = filteredRoutes.value.map(() => false)
        internalMinimized.value = true
      }
    }

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name
      }
      return section.children.some(({ name }) => route.name === name)
    }

    const setActiveExpand = () =>
      (value.value = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const color = computed(() => getColor('background-secondary'))
    // Active item: same hue as header (primary), subtle background + clear text
    const activeColor = computed(() => {
      const isDark = themeStore.theme === 'dark'
      return colorToRgba(getColor('primary'), isDark ? 0.35 : 0.12)
    })

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // Sync internal state with global store when icon is clicked in auto mode
    watch(
      () => globalStore.isSidebarMinimized,
      (newValue) => {
        if (globalStore.autoCollapseEnabled && !props.mobile) {
          if (newValue) {
            // Save current expanded state before collapsing
            savedExpandedState.value = [...value.value]
            // Close all expanded menus when collapsing
            value.value = filteredRoutes.value.map(() => false)
          } else {
            // Restore saved expanded state when expanding, and ensure active route is expanded
            if (savedExpandedState.value.length > 0) {
              const activeExpanded = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
              // Merge saved state with active route expansion (active routes should always be expanded)
              value.value = savedExpandedState.value.map((saved, index) => saved || activeExpanded[index])
            } else {
              // If no saved state, expand active route
              setActiveExpand()
            }
          }
          internalMinimized.value = newValue
        } else if (!globalStore.autoCollapseEnabled && !props.mobile) {
          // Manual mode - handle collapse/expand
          if (newValue) {
            // Save current expanded state before collapsing
            savedExpandedState.value = [...value.value]
            // Close all expanded menus when collapsing
            value.value = filteredRoutes.value.map(() => false)
          } else {
            // Restore saved expanded state when expanding, and ensure active route is expanded
            if (savedExpandedState.value.length > 0) {
              const activeExpanded = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route))
              // Merge saved state with active route expansion (active routes should always be expanded)
              value.value = savedExpandedState.value.map((saved, index) => saved || activeExpanded[index])
            } else {
              // If no saved state, expand active route
              setActiveExpand()
            }
          }
        }
      },
    )

    // When switching to manual mode, sync the current state to global store
    watch(
      () => globalStore.autoCollapseEnabled,
      (isAutoEnabled) => {
        if (!isAutoEnabled && !props.mobile) {
          // Switching to manual mode - preserve current expanded state
          globalStore.isSidebarMinimized = !internalMinimized.value
        } else if (isAutoEnabled && !props.mobile) {
          // Switching to auto mode - sync internal state with global store
          internalMinimized.value = globalStore.isSidebarMinimized
        }
      },
    )

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // Prevent scroll events on all scrollable elements within sidebar
    const preventAllScrolling = (e: Event) => {
      const target = e.target as HTMLElement
      const sidebar = target.closest('.va-sidebar')
      if (sidebar && isMinimized.value && value.value.some((expanded) => expanded)) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }
    }

    const preventScrollEvent = (e: Event) => {
      if (isMinimized.value && value.value.some((expanded) => expanded)) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }
    }

    // Toggle body scrollbars when sidebar is minimized and a menu is expanded
    watch(
      [isMinimized, value],
      ([minimized, currentValue]) => {
        const body = document.body
        if (!body) return

        const hasExpanded = currentValue.some((expanded) => expanded)

        if (minimized && hasExpanded) {
          body.classList.add('sidebar-expanded-no-scroll')
        } else {
          body.classList.remove('sidebar-expanded-no-scroll')
        }
      },
      { deep: true },
    )

    // Function to prevent scrollbars without breaking collapse animation
    const preventScrollbars = () => {
      // Handle collapse body wrappers
      const bodyWrappers = document.querySelectorAll('.va-collapse__body-wrapper')
      bodyWrappers.forEach((wrapper) => {
        const element = wrapper as HTMLElement
        // Always set overflow to visible to prevent scrollbars
        // This is the key - overflow visible allows content to expand beyond height
        element.style.overflow = 'visible'
        element.style.overflowY = 'visible'
        element.style.overflowX = 'visible'
        element.style.maxHeight = 'none'
        // Don't touch height - let the collapse component manage it for animations
      })

      // Handle sidebar menu
      const sidebarMenus = document.querySelectorAll('.va-sidebar__menu')
      sidebarMenus.forEach((menu) => {
        const element = menu as HTMLElement
        element.style.overflow = 'visible'
        element.style.overflowY = 'visible'
        element.style.overflowX = 'visible'
      })
    }

    onMounted(() => {
      loadEnabledModules()
      // Add event listeners to prevent scrolling on sidebar and all children
      const sidebarElement = document.querySelector('.va-sidebar')
      if (sidebarElement) {
        // Prevent on sidebar itself
        sidebarElement.addEventListener('wheel', preventAllScrolling, { passive: false, capture: true })
        sidebarElement.addEventListener('touchmove', preventAllScrolling, { passive: false, capture: true })
        sidebarElement.addEventListener('scroll', preventScrollEvent, { passive: false, capture: true })

        // Prevent on all children
        const allChildren = sidebarElement.querySelectorAll('*')
        allChildren.forEach((child) => {
          child.addEventListener('wheel', preventAllScrolling, { passive: false, capture: true })
          child.addEventListener('touchmove', preventAllScrolling, { passive: false, capture: true })
          child.addEventListener('scroll', preventScrollEvent, { passive: false, capture: true })
        })

        // Prevent scrollbars on collapse body wrappers
        preventScrollbars()

        // Use MutationObserver to watch for changes to collapse body wrappers
        const observer = new MutationObserver(() => {
          preventScrollbars()
        })

        // Observe the sidebar for changes
        observer.observe(sidebarElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style'],
        })

        // Also periodically check (as a fallback) since transitions might update styles
        const intervalId = setInterval(() => {
          preventScrollbars()
        }, 100)

        // Store interval ID for cleanup
        ;(sidebarElement as any).__heightConstraintInterval = intervalId
        ;(sidebarElement as any).__heightConstraintObserver = observer
      }
    })

    onBeforeUnmount(() => {
      const sidebarElement = document.querySelector('.va-sidebar')
      if (sidebarElement) {
        sidebarElement.removeEventListener('wheel', preventAllScrolling)
        sidebarElement.removeEventListener('touchmove', preventAllScrolling)
        sidebarElement.removeEventListener('scroll', preventScrollEvent)

        const allChildren = sidebarElement.querySelectorAll('*')
        allChildren.forEach((child) => {
          child.removeEventListener('wheel', preventAllScrolling)
          child.removeEventListener('touchmove', preventAllScrolling)
          child.removeEventListener('scroll', preventScrollEvent)
        })

        // Clean up interval and observer
        const intervalId = (sidebarElement as any).__heightConstraintInterval
        const observer = (sidebarElement as any).__heightConstraintObserver
        if (intervalId) {
          clearInterval(intervalId)
        }
        if (observer) {
          observer.disconnect()
        }
      }
    })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      isMinimized,
      handleMouseEnter,
      handleMouseLeave,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.va-sidebar) {
  transition: width 0.3s ease;
  height: auto !important;
  min-height: 100vh;
  max-height: none !important;
  overflow: visible !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
  overscroll-behavior: none !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
  left: 0 !important;

  &::-webkit-scrollbar {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  &::-webkit-scrollbar-track {
    display: none !important;
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    display: none !important;
    background: transparent !important;
  }

  /* Hide all scrollbars globally within sidebar */
  * {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
  }

  *::-webkit-scrollbar {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  *::-webkit-scrollbar-track {
    display: none !important;
    background: transparent !important;
  }

  *::-webkit-scrollbar-thumb {
    display: none !important;
    background: transparent !important;
  }

  :deep(.va-sidebar__content) {
    /* Remove all height and overflow constraints to allow natural expansion */
    height: auto !important;
    min-height: 100vh !important;
    max-height: none !important;
    overflow: visible !important;
    overflow-y: visible !important;
    overflow-x: visible !important;
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
    overscroll-behavior: none !important;
    padding-left: 0 !important;
  }

  /* Override any Vuestic layout constraints */
  :deep(.va-layout__left),
  :deep(.va-layout__left > *) {
    height: auto !important;
    max-height: none !important;
  }

  :deep(.va-sidebar__content::-webkit-scrollbar) {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Remove scrollbars from sidebar menu */
  :deep(.va-sidebar__menu) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
    overscroll-behavior: none !important;
    padding-left: 0 !important;
  }

  :deep(.va-sidebar__menu::-webkit-scrollbar) {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Remove overflow constraints from accordion */
  :deep(.va-accordion) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    height: auto !important;
    min-height: auto !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    overscroll-behavior: none !important;
  }

  /* Remove overflow constraints from collapse components */
  :deep(.va-collapse) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    overscroll-behavior: none !important;
  }

  /* Remove overflow constraints from collapse body/content */
  /* Only set overflow to visible - let the component manage height for animations */
  :deep(.va-collapse__body) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
    overscroll-behavior: none !important; /* Prevent scroll chaining */
    touch-action: none !important; /* Prevent touch scrolling */
    position: relative !important;
    /* Don't set height: auto - let the collapse component manage it for smooth animations */
  }

  /* Ensure the internal body wrapper used for animation doesn't create scrollbars */
  /* Only set overflow to visible - let the component manage height for animations */
  :deep(.va-collapse__body-wrapper) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    /* Don't set height: auto - let the collapse component manage it for smooth animations */
  }

  :deep(.va-collapse__body-wrapper::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  :deep(.va-collapse__content) {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
    overscroll-behavior: none !important; /* Prevent scroll chaining */
    touch-action: none !important; /* Prevent touch scrolling */
    position: relative !important;
    /* Don't set height: auto - let the collapse component manage it for smooth animations */
  }

  /* Hide scrollbars on all nested elements */
  :deep(.va-collapse__body::-webkit-scrollbar),
  :deep(.va-collapse__content::-webkit-scrollbar),
  :deep(.va-accordion::-webkit-scrollbar),
  :deep(.va-sidebar__content::-webkit-scrollbar),
  :deep(.va-collapse::-webkit-scrollbar) {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Hide scrollbars on sidebar item containers */
  :deep(.va-sidebar-item),
  :deep(.va-sidebar-item__content),
  :deep(.va-sidebar-item__title) {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
  }

  :deep(.va-sidebar-item::-webkit-scrollbar),
  :deep(.va-sidebar-item__content::-webkit-scrollbar),
  :deep(.va-sidebar-item__title::-webkit-scrollbar) {
    display: none !important; /* Chrome, Safari and Opera */
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Hide scrollbars on wrapper divs inside collapse body */
  :deep(.va-collapse__body > div),
  :deep(.va-collapse__content > div) {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }

  :deep(.va-collapse__body > div::-webkit-scrollbar),
  :deep(.va-collapse__content > div::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Target the wrapper divs specifically */
  :deep(.collapse-child-wrapper) {
    overflow: visible !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    overscroll-behavior: none !important;
    touch-action: none !important;
  }

  :deep(.collapse-child-wrapper::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  /* Prevent scrolling on sidebar items */
  :deep(.va-sidebar-item) {
    overscroll-behavior: none !important;
    touch-action: none !important;
  }

  /* Prevent all scrolling behavior on expanded menus */
  :deep(.va-collapse__body),
  :deep(.va-collapse__content),
  :deep(.va-accordion) {
    overscroll-behavior: none !important;
    touch-action: none !important;
    -webkit-overflow-scrolling: auto !important;
    overflow: visible !important;
  }

  /* Disable pointer events that might trigger scrolling */
  :deep(.va-collapse__body *),
  :deep(.va-collapse__content *) {
    overscroll-behavior: none !important;
  }

  /* When sidebar is minimized and menu is expanded, prevent all scrolling */
  &.va-sidebar--minimized {
    :deep(.va-collapse__body),
    :deep(.va-collapse__content) {
      overflow: visible !important;
      overscroll-behavior: none !important;
      touch-action: none !important;
      pointer-events: auto !important;
    }
  }

  /* Completely disable scrolling on all elements when minimized with expanded menu */
  :deep(*) {
    overscroll-behavior: none !important;
  }
}

/* Global prevention of scrolling when sidebar is minimized with expanded menu */
.va-sidebar--minimized .va-collapse__body,
.va-sidebar--minimized .va-collapse__content,
.va-sidebar--minimized * {
  overscroll-behavior: none !important;
  touch-action: none !important;
}

/* Sidebar item: flat, full-width (no gaps) */
:deep(.va-sidebar-item) {
  width: 100%;
  border-radius: 0 !important;
  margin: 0 !important;
  box-sizing: border-box;
  border-left: 4px solid transparent; /* prevents layout shift on active */
}

/* Active state: flat left border full height, no rounded corners, faint header (primary) color */
:deep(.va-sidebar-item--active) {
  border-radius: 0 !important;
  border-left: 4px solid color-mix(in srgb, var(--va-primary) 45%, transparent);
  background-color: color-mix(in srgb, var(--va-primary) 10%, transparent);
  box-shadow: none;
}
:deep(.va-sidebar-item--active .va-icon),
:deep(.va-sidebar-item--active .va-sidebar-item__title),
:deep(.va-sidebar-item--active .va-sidebar-item__content) {
  color: var(--va-primary) !important;
  font-weight: 600;
}

/* Dark theme sidebar */
[data-theme='dark'] :deep(.va-sidebar) {
  background-color: var(--va-background-secondary);
}

[data-theme='dark'] :deep(.va-sidebar-item) {
  color: var(--va-text-secondary);
}

[data-theme='dark'] :deep(.va-sidebar-item__content) {
  color: var(--va-text-secondary);
}

[data-theme='dark'] :deep(.va-sidebar-item--active) {
  border-radius: 0 !important;
  border-left: 4px solid color-mix(in srgb, var(--va-primary) 55%, transparent);
  background-color: color-mix(in srgb, var(--va-primary) 18%, transparent) !important;
}
[data-theme='dark'] :deep(.va-sidebar-item--active .va-icon),
[data-theme='dark'] :deep(.va-sidebar-item--active .va-sidebar-item__title),
[data-theme='dark'] :deep(.va-sidebar-item--active .va-sidebar-item__content) {
  color: var(--va-primary) !important;
  font-weight: 600;
}

/* Flat menu layout: remove padding gaps at top/edges */
:deep(.va-sidebar__content),
:deep(.va-sidebar__menu),
:deep(.va-accordion) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Child wrappers shouldn't introduce vertical gaps */
:deep(.collapse-child-wrapper) {
  margin: 0 !important;
  padding: 0 !important;
}

/* Flat hover (no gaps, no rounding) */
:deep(.va-sidebar-item:hover) {
  border-radius: 0 !important;
  background-color: color-mix(in srgb, var(--va-primary) 7%, transparent);
}

[data-theme='dark'] :deep(.va-sidebar-item:hover) {
  background-color: color-mix(in srgb, var(--va-primary) 14%, transparent);
}
</style>
